import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import AdminSubnav from "@/components/app/AdminSubnav";
import { CheckCircle2, XCircle, RefreshCw, Mail, AlertTriangle } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function AdminEmailDiagnostic() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-diagnostic");
      setData(data);
      toast.success("Diagnostic effectué");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du diagnostic");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { run(); }, []);

  const cfg = data?.config || {};
  const senderOK = cfg.SENDER_EMAIL && !cfg.SENDER_in_test_mode;

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <AdminSubnav />
      <p className="overline mb-2">Administration</p>
      <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none mb-3">
        Diagnostic des courriels
      </h1>
      <p className="text-gray-500 mb-6 max-w-lg">
        Vérifie en temps réel la configuration d'envoi et teste un courriel à chaque administrateur.
      </p>

      <button
        className="btn-primary mb-6"
        onClick={run}
        disabled={loading}
        data-testid="run-diagnostic"
      >
        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        {loading ? "Test en cours..." : "Relancer le diagnostic"}
      </button>

      {data && (
        <div className="space-y-6">
          {/* Configuration card */}
          <div className="ra-card p-5">
            <h2 className="font-heading font-black text-lg tracking-tight mb-3">Configuration Resend</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className={`p-3 rounded ${cfg.RESEND_API_KEY_set ? "bg-green-50 dark:bg-green-950/30" : "bg-red-50 dark:bg-red-950/30"}`}>
                <div className="text-xs text-gray-500 uppercase">Clé API</div>
                <div className="font-medium flex items-center gap-2">
                  {cfg.RESEND_API_KEY_set
                    ? <><CheckCircle2 size={14} className="text-green-600" /> Configurée ({cfg.RESEND_API_KEY_prefix})</>
                    : <><XCircle size={14} className="text-red-600" /> NON configurée</>}
                </div>
              </div>
              <div className={`p-3 rounded ${senderOK ? "bg-green-50 dark:bg-green-950/30" : "bg-red-50 dark:bg-red-950/30"}`}>
                <div className="text-xs text-gray-500 uppercase">Adresse expéditrice</div>
                <div className="font-medium flex items-center gap-2 break-all">
                  {senderOK
                    ? <><CheckCircle2 size={14} className="text-green-600" /> {cfg.SENDER_EMAIL}</>
                    : <><AlertTriangle size={14} className="text-red-600" /> {cfg.SENDER_EMAIL || "(vide)"}</>}
                </div>
                {cfg.SENDER_in_test_mode && (
                  <div className="text-xs text-red-700 dark:text-red-400 mt-1">⚠️ Mode test — n'envoie qu'au propriétaire du compte Resend</div>
                )}
              </div>
            </div>
          </div>

          {/* Admins list */}
          <div className="ra-card p-5">
            <h2 className="font-heading font-black text-lg tracking-tight mb-3">
              Administrateurs en base ({data.admins_in_db?.length || 0})
            </h2>
            {data.admins_in_db?.length === 0 ? (
              <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 p-3 rounded">
                ❌ <strong>Aucun administrateur trouvé en base de données.</strong> Le bouton "Prêt à valider" ne peut envoyer aucun courriel s'il n'y a pas d'admin. Allez dans <a href="/admin" className="underline">Utilisateurs</a> et promouvez quelqu'un en admin.
              </div>
            ) : (
              <div className="space-y-2">
                {data.admins_in_db.map((u) => {
                  const result = data.test_send_results?.find((r) => r.email === u.email);
                  const ok = result?.status === "sent";
                  return (
                    <div key={u.user_id} className={`p-3 rounded flex items-start justify-between gap-3 ${ok ? "bg-green-50 dark:bg-green-950/30" : "bg-red-50 dark:bg-red-950/30"}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium flex items-center gap-2">
                          {ok ? <CheckCircle2 size={14} className="text-green-600 shrink-0" /> : <XCircle size={14} className="text-red-600 shrink-0" />}
                          <span className="truncate">{u.name || "(sans nom)"}</span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 break-all ml-6">{u.email}</div>
                        {result?.error && (
                          <div className="text-xs text-red-700 dark:text-red-400 ml-6 mt-1 break-words">
                            <strong>Erreur Resend :</strong> {result.error}
                          </div>
                        )}
                        {ok && result.message_id && (
                          <div className="text-xs text-green-700 dark:text-green-400 ml-6 mt-0.5">id: {result.message_id}</div>
                        )}
                      </div>
                      <div className="text-xs uppercase font-bold shrink-0">
                        {ok ? "✅ Envoyé" : "❌ Échec"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* VPs */}
          {data.vps_in_db?.length > 0 && (
            <div className="ra-card p-5">
              <h2 className="font-heading font-black text-lg tracking-tight mb-3">
                Vice-présidents ({data.vps_in_db.length})
              </h2>
              <div className="space-y-1 text-sm">
                {data.vps_in_db.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between py-1">
                    <span>{u.name || "(sans nom)"}</span>
                    <span className="text-gray-500 text-xs break-all">{u.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comptable */}
          <div className="ra-card p-5">
            <h2 className="font-heading font-black text-lg tracking-tight mb-3">Comptable</h2>
            {data.accountant?.email ? (
              <div className="text-sm">
                <span className="font-medium">{data.accountant.name}</span> — <span className="text-gray-500">{data.accountant.email}</span>
              </div>
            ) : (
              <div className="text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
                ⚠️ Aucun comptable configuré. Allez dans <a href="/admin/settings" className="underline">Paramètres</a> pour le définir.
              </div>
            )}
          </div>

          <div className="ra-card p-4 text-xs text-gray-500 bg-blue-50 dark:bg-blue-950/30 border-l-4 border-[#002FA7]">
            <Mail size={14} className="inline mr-1" />
            <strong>Astuce :</strong> chaque admin avec ✅ devrait avoir reçu à l'instant un courriel "🧪 Test diagnostic Riva-Avir". Demandez-leur de vérifier leur boîte de réception ET leur dossier <strong>Spam/Indésirables</strong> (les premiers courriels de noreply@vosfacturesriva.com peuvent atterrir là-bas chez certains fournisseurs).
          </div>
        </div>
      )}
    </Layout>
  );
}
